<template>
  <ModalComponent
    :submitButtonText="t('bulk_buy.confirm_and_submit')"
    :hideSubmitButton="true"
    size="xl"
    @setForceToggle="setForceToggle"
    @close="handleCancel"
  >
    <template #title>
      {{ t("bulk_buy.review_order_title") }}
    </template>

    <template #body>
      <div class="py-3">
        <!-- Order summary -->
        <div class="eyebrow-label mb-2">
          {{ t("bulk_buy.order_summary") }}
        </div>
        <div class="border rounded-3 p-3 mb-4">
          <div class="row g-3">
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.total_volume") }}
              </div>
              <div class="font-serif summary-figure">
                {{ totalVolume.toLocaleString() }} MWh
              </div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.total_price") }}
              </div>
              <div class="font-serif summary-figure text-primary-emphasis">
                {{ totalKnownVolume > 0 ? formatEuro(totalKnownPrice) : "—" }}
              </div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.average_unit_price") }}
              </div>
              <div class="font-serif summary-figure">
                <template v-if="averagePricePerMwh !== null">
                  {{ formatEuro(averagePricePerMwh)
                  }}<span class="text-secondary small fw-normal"> /MWh</span>
                </template>
                <template v-else>—</template>
              </div>
            </div>
          </div>
        </div>

        <!-- OTC disclaimer: prices are indicative bids, not firm ones.
             Only shown when the modal actually displays euro amounts. -->
        <div v-if="totalKnownVolume > 0" class="advisory-note mb-4">
          <FontAwesomeIcon
            :icon="['far', 'circle-info']"
            class="flex-shrink-0 mt-1"
          />
          <span class="small">
            {{ t("bulk_buy.indicative_bid_note") }}
            <span v-if="marketDataDate" class="d-block text-secondary mt-1">
              {{
                t("bulk_buy.market_levels_as_of", {
                  date: marketDataDateLabel(marketDataDate),
                })
              }}
            </span>
          </span>
        </div>

        <!-- OTHER mode skips automatic pricing entirely, so no agent_reach_out rows exist;
             the whole order is a custom RFQ and needs the approval-gate explanation itself. -->
        <div v-if="isOtherMode" class="advisory-note mb-4">
          <FontAwesomeIcon
            :icon="['far', 'circle-info']"
            class="flex-shrink-0 mt-1"
          />
          <div>
            <span class="fw-semibold">{{ t("bulk_buy.rfq_notice") }}:</span>
            {{ t("bulk_buy.custom_quote_reassurance") }}
          </div>
        </div>

        <!-- RFQ warning for manual-quote lines -->
        <div v-if="hasAgentReachOut" class="advisory-note mb-4">
          <FontAwesomeIcon
            :icon="['far', 'circle-info']"
            class="flex-shrink-0 mt-1"
          />
          <div>
            <span class="fw-semibold">{{ t("bulk_buy.rfq_notice") }}:</span>
            {{ agentReachOutVolume.toLocaleString() }} MWh ({{
              t("bulk_buy.illiquid_warning", agentReachOutRows.length)
            }})
          </div>
        </div>

        <UnsupportedRegistryNotice
          v-if="unsupportedRegistryEntries.length > 0"
          class="mb-4"
          :entries="unsupportedRegistryEntries"
        >
          <div
            class="d-flex flex-column flex-sm-row gap-2 mt-3"
            role="group"
            :aria-label="t('bulk_buy.cancellation_mode_selection_aria')"
          >
            <input
              id="cancellation-mode-agent"
              v-model="globalCancellationMode"
              type="radio"
              class="btn-check"
              name="cancellation-mode-global"
              value="COMPLIANT_AGENT"
              autocomplete="off"
            />
            <label
              class="btn btn-outline-primary"
              for="cancellation-mode-agent"
            >
              <FontAwesomeIcon icon="user" class="me-1" />
              {{ t("bulk_buy.cancellation_option_agent") }}
            </label>

            <input
              id="cancellation-mode-aib"
              v-model="globalCancellationMode"
              type="radio"
              class="btn-check"
              name="cancellation-mode-global"
              value="ANY_AIB_REGISTRY"
              autocomplete="off"
            />
            <label
              class="btn btn-outline-secondary"
              for="cancellation-mode-aib"
            >
              <FontAwesomeIcon icon="globe" class="me-1" />
              {{ t("bulk_buy.cancellation_option_any_registry") }}
            </label>
          </div>
        </UnsupportedRegistryNotice>

        <!-- Shared cancellation settings -->
        <div class="eyebrow-label mb-2">
          {{ t("bulk_buy.shared_cancellation_settings") }}
        </div>
        <div class="border rounded-3 p-3 mb-4">
          <div class="row g-3">
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.beneficiary_type") }}
              </div>
              <div class="fw-medium">
                {{
                  t(
                    `bulk_buy.beneficiary_types.${cancellationSettings.beneficiary_type}`
                  )
                }}
              </div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.consumption_period") }}
              </div>
              <div class="fw-medium tabular">{{ periodLabel }}</div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("bulk_buy.purchase_settings") }}
              </div>
              <div class="fw-medium">{{ sourcingLabel }}</div>
            </div>
          </div>
          <div
            v-if="globalSettings.custom_requirement"
            class="mt-3 pt-3 border-top"
          >
            <div class="text-secondary small mb-1">
              {{ t("bulk_buy.custom_requirement_label") }}
            </div>
            <div class="fw-medium">{{ globalSettings.custom_requirement }}</div>
          </div>
        </div>

        <!-- Beneficiaries review table -->
        <div class="d-flex align-items-center gap-2 mb-2">
          <span class="eyebrow-label">{{
            t("bulk_buy.destination_companies")
          }}</span>
          <span
            class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
            >{{ companies.length }}</span
          >
        </div>
        <div class="table-responsive review-table">
          <DataTable
            :value="companies"
            dataKey="temp_id"
            class="p-datatable-sm border rounded"
          >
            <Column :header="t('bulk_buy.country')">
              <template #body="{ data }">
                {{ data.country }}
              </template>
            </Column>

            <Column :header="t('bulk_buy.eac_type')" style="width: 160px">
              <template #body="{ data }">
                {{ eacTypeFor(data) }}
              </template>
            </Column>

            <Column
              header="MWh"
              headerStyle="text-align: right;"
              bodyStyle="text-align: right;"
              style="width: 90px"
            >
              <template #body="{ data }">
                <span class="tabular">{{ data.volume.toLocaleString() }}</span>
              </template>
            </Column>

            <Column
              :header="t('bulk_buy.price')"
              headerStyle="text-align: right;"
              bodyStyle="text-align: right;"
              style="width: 130px"
            >
              <template #body="{ data }">
                <span
                  v-if="data.pricing_state.status === 'success'"
                  class="fw-semibold tabular"
                >
                  {{ formatEuro(data.pricing_state.price) }}
                </span>
                <QuotePill
                  v-else
                  :kind="isOtherMode ? 'by-quote' : 'manual-quote'"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn-ghost" @click="handleCancel">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="isSubmitting || !allCancellationModesChosen"
        @click="handleConfirm"
      >
        <span
          v-if="isSubmitting"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("bulk_buy.confirm_and_submit") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatEuro, formatShortMonthYear } from "@/composables/formatUtils";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import UnsupportedRegistryNotice from "../common/UnsupportedRegistryNotice.vue";
import QuotePill from "./QuotePill.vue";
import {
  CancellationMode,
  eacTypeLabel,
  effectiveEacType,
  marketDataDateLabel,
  PurchaseMode,
} from "./bulkBuyTypes";
import type {
  CompanyRow,
  GlobalCancellationSettings,
  GlobalPurchaseSettings,
} from "./bulkBuyTypes";
import { useBulkBuyTotals } from "./useBulkBuyTotals";

interface Props {
  companies: (CompanyRow & { temp_id: number })[];
  globalSettings: GlobalPurchaseSettings;
  cancellationSettings: GlobalCancellationSettings;
  registrationAuthorities?: Record<string, string[]> | null;
  eacTypeLabels?: Record<string, string> | null;
  isSubmitting?: boolean;
}

interface Emits {
  confirm: [cancellationModes: Record<number, CancellationMode>];
  cancel: [];
}

const props = withDefaults(defineProps<Props>(), {
  isSubmitting: false,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const forceToggle = ref<((value: boolean) => void) | null>(null);
const globalCancellationMode = ref<CancellationMode | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

const isOtherMode = computed(
  () => props.globalSettings.purchase_mode === PurchaseMode.OTHER
);

const sourcingLabel = computed(() =>
  t(`bulk_buy.purchase_mode.${props.globalSettings.purchase_mode}`)
);

const periodLabel = computed(() => {
  const { period_start, period_end } = props.cancellationSettings;
  if (!period_start || !period_end) return "—";
  return `${formatShortMonthYear(period_start)} – ${formatShortMonthYear(period_end)}`;
});

const eacTypeFor = (row: CompanyRow): string => {
  const authority = effectiveEacType(row, props.registrationAuthorities);
  return authority ? eacTypeLabel(authority, props.eacTypeLabels) : "—";
};

// Batch-level as-of date, shown only when every priced row carries one and they all
// fall on the same calendar day — mixed or partially dated batches keep their per-row
// dates in the pricing cells instead.
const marketDataDate = computed(() => {
  const dates: string[] = [];
  for (const company of props.companies) {
    if (company.pricing_state.status !== "success") continue;
    if (!company.pricing_state.price_date) return null;
    dates.push(company.pricing_state.price_date);
  }
  if (dates.length === 0) return null;
  const days = new Set(dates.map((date) => date.slice(0, 10)));
  return days.size === 1 ? dates[0] : null;
});

const companiesWithoutCancellationSupport = computed(() =>
  props.companies.filter(
    (c) =>
      c.pricing_state.status === "success" &&
      c.pricing_state.cancellation_supported === false
  )
);

const unsupportedRegistryEntries = computed(() =>
  companiesWithoutCancellationSupport.value.map((company) => ({
    key: String(company.temp_id),
    name: `${company.volume.toLocaleString()} MWh`,
    region: company.country,
  }))
);

const allCancellationModesChosen = computed(() => {
  if (companiesWithoutCancellationSupport.value.length === 0) return true;
  return globalCancellationMode.value !== null;
});

const {
  totalVolume,
  totalKnownVolume,
  totalKnownPrice,
  agentReachOutRows,
  agentReachOutVolume,
} = useBulkBuyTotals(() => props.companies);

const hasAgentReachOut = computed(() => agentReachOutRows.value.length > 0);

const averagePricePerMwh = computed(() => {
  if (totalKnownVolume.value > 0 && totalKnownPrice.value > 0) {
    return totalKnownPrice.value / totalKnownVolume.value;
  }
  return null;
});

const show = () => {
  forceToggle.value?.(true);
};

const hide = () => {
  forceToggle.value?.(false);
};

const handleConfirm = () => {
  const modes: Record<number, CancellationMode> = {};
  if (globalCancellationMode.value) {
    for (const company of companiesWithoutCancellationSupport.value) {
      modes[company.temp_id] = globalCancellationMode.value;
    }
  }
  emit("confirm", modes);
};

const handleCancel = () => {
  emit("cancel");
  hide();
};

defineExpose({
  show,
  hide,
});
</script>

<style scoped lang="scss">
.summary-figure {
  font-size: 1.25rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.tabular {
  font-variant-numeric: tabular-nums;
}

.review-table {
  :deep(.pv-datatable-header-cell),
  :deep(.pv-datatable-body-cell) {
    white-space: normal;
    overflow: visible;
  }
}
</style>
